// MaturaLinearZeroAndValueClosedTask.js
// Zadanie maturalne zamknięte: funkcja liniowa określona
// przez miejsce zerowe i wartość w 0, wybór wzoru g(x).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

// ===== Helpers =====

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
};

// format mieszany jak w innych zadaniach
function formatFraction(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  let g = gcd(num, den);
  let N = num / g;
  let D = den / g;

  const sign = N < 0 ? "-" : "";
  N = Math.abs(N);

  if (N % D === 0) {
    return `${sign}${N / D}`;
  }

  const whole = Math.floor(N / D);
  const rest = N % D;

  if (whole > 0) {
    return `${sign}${whole}\\dfrac{${rest}}{${D}}`;
  }
  return `${sign}\\dfrac{${rest}}{${D}}`;
}

// budowanie latexu dla a, b w równaniu g(x)
function buildLineLatex(aNum, aDen, bNum, bDen) {
  // a
  if (aDen < 0) {
    aDen = -aDen;
    aNum = -aNum;
  }
  const aVal = aNum / aDen;
  const aFracLatex = formatFraction(aNum, aDen);

  let aTerm;
  if (aVal === 1) aTerm = "x";
  else if (aVal === -1) aTerm = "-x";
  else aTerm = `${aFracLatex}x`;

  // b
  if (bDen < 0) {
    bDen = -bDen;
    bNum = -bNum;
  }
  const bVal = bNum / bDen;
  const bFracLatex = formatFraction(bNum, bDen);
  const sign = bVal >= 0 ? "+" : "-";
  const bAbsLatex = bFracLatex.replace("-", "");

  return `g(x) = ${aTerm} ${sign} ${bAbsLatex}`;
}

// ===== GENERATOR =====

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 13:
 *   miejsce zerowe (−3), g(0) = −3/2  →  A. g(x) = −1/2 x − 3/2
 */
function createOriginalTask() {
  const x0 = -3;
  const bNum = -3;
  const bDen = 2;
  const aNum = -1;
  const aDen = 2;

  const options = [
    { label: "A", latex: "g(x) = -\\dfrac{1}{2}x - \\dfrac{3}{2}", isCorrect: true },
    { label: "B", latex: "g(x) = -\\dfrac{1}{2}x + \\dfrac{3}{2}", isCorrect: false },
    { label: "C", latex: "g(x) = \\dfrac{1}{2}x - \\dfrac{3}{2}", isCorrect: false },
    { label: "D", latex: "g(x) = \\dfrac{1}{2}x + \\dfrac{3}{2}", isCorrect: false },
  ];
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaLinearZeroAndValueClosedTask",
    data: { x0, bNum, bDen, aNum, aDen, options, correctIndex },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 13,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  // losujemy miejsce zerowe x0 (nie 0)
  let x0;
  do {
    x0 = randInt(-5, 5);
  } while (x0 === 0);

  // losujemy wartość w 0, czyli g(0) = b (prosty ułamek / liczba)
  const denOptions = [1, 2]; // całkowite lub połówki
  let bDen = pick(denOptions);
  let bNum;
  do {
    bNum = randInt(-6, 6);
  } while (bNum === 0); // unikamy stałej zero

  // upraszczamy b
  let g = gcd(bNum, bDen);
  bNum /= g;
  bDen /= g;

  // z warunku g(x0) = 0: 0 = a x0 + b → a = -b / x0
  let aNum = -bNum;
  let aDen = bDen * x0;
  if (aDen < 0) {
    aDen = -aDen;
    aNum = -aNum;
  }
  const g2 = gcd(aNum, aDen);
  aNum /= g2;
  aDen /= g2;

  // poprawna odpowiedź
  const correctLatex = buildLineLatex(aNum, aDen, bNum, bDen);

  // distractor 1: zły znak przy a
  const wrong1Latex = buildLineLatex(-aNum, aDen, bNum, bDen);
  // distractor 2: zły znak przy b
  const wrong2Latex = buildLineLatex(aNum, aDen, -bNum, bDen);
  // distractor 3: oba znaki odwrócone
  const wrong3Latex = buildLineLatex(-aNum, aDen, -bNum, bDen);

  let rawOptions = [
    { latex: correctLatex, isCorrect: true },
    { latex: wrong1Latex, isCorrect: false },
    { latex: wrong2Latex, isCorrect: false },
    { latex: wrong3Latex, isCorrect: false },
  ];

  // proste wymieszanie, bez duplikatów (tu i tak zawsze różne)
  rawOptions = [...rawOptions].sort(() => Math.random() - 0.5);

  const options = rawOptions.map((opt, idx) => ({
    label: LETTERS[idx],
    latex: opt.latex,
    isCorrect: opt.isCorrect,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaLinearZeroAndValueClosedTask",
    data: {
      x0,
      bNum,
      bDen,
      aNum,
      aDen,
      options,
      correctIndex,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_13" },
  };
}

// ===== RENDER TREŚCI =====

export function renderTask({ data }) {
  const { x0, bNum, bDen, options } = data;
  const bLatex = formatFraction(bNum, bDen);

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Miejscem zerowym funkcji liniowej <InlineMath math={"g"} /> jest liczba{" "}
        <InlineMath math={`(${x0})`} />.
      </p>
      <p>
        Dla argumentu{" "}
        <InlineMath math={"0"} /> funkcja <InlineMath math={"g"} /> przyjmuje
        wartość <InlineMath math={`(${bLatex})`} />.
      </p>
      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
        Funkcja <InlineMath math={"g"} /> jest określona wzorem:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== INPUT (ABCD) =====

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ===== SPRAWDZANIE / ODPOWIEDZI =====

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ===== WYJAŚNIENIE =====

export function renderExplanation({ data }) {
  const { x0, bNum, bDen, aNum, aDen } = data;

  const bLatex = formatFraction(bNum, bDen);
  const aLatex = formatFraction(aNum, aDen);
  const lineLatex = buildLineLatex(aNum, aDen, bNum, bDen);

  const bValue = `g(0) = ${bLatex}`;
  const zeroEq = String.raw`0 = g(${x0}) = a\cdot ${x0} + ${bLatex}`;
  const aEq = String.raw`a = -\dfrac{${bLatex}}{${x0}} = ${aLatex}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Z warunku{" "}
        <InlineMath math={"g(0)"} /> odczytujemy wyraz wolny:
      </p>
      <BlockMath math={bValue} />

      <p>
        <strong>Krok 2.</strong> Z warunku miejsca zerowego{" "}
        <InlineMath math={`x_0 = ${x0}`} /> mamy:
      </p>
      <BlockMath math={zeroEq} />
      <p>Stąd:</p>
      <BlockMath math={aEq} />

      <p>
        <strong>Krok 3.</strong> Równanie funkcji liniowej ma postać:
      </p>
      <BlockMath math={lineLatex} />

      <p>
        Zatem poprawną odpowiedzią jest dokładnie ten wzór spośród <InlineMath math="A,B,C,D" />,
        który zgadza się z powyższym równaniem.
      </p>
    </div>
  );
}
